/**
 * Spec 026 + 027 — Wizard-State-Context.
 *
 * Tracks per-session onboarding state across Welcome → ProfileSetup → TeamFlow.
 *
 * 027 extends the state with `TeamFlowState` (mode, codes, team-id, errors)
 * and persists `clavis_wizard_team_state` into sessionStorage so a refresh on
 * any team-flow step restores the wizard cleanly. NEVER persists email/password
 * (Constitution II / FR-R7).
 */
import React, {
  createContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { useAuth } from '../auth/useAuth';

export type WizardPathKind = 'no-code' | 'with-code' | 'direct-register';

/** Spec 027 FR-009a + US1/US2/US3 — team-flow runtime state. */
export interface TeamFlowState {
  /** Find-step selection. */
  mode: 'create' | 'join' | null;

  /** Join-path: raw code typed/scanned by the user (already uppercased). */
  enteredCode: string;
  joinError:
    | 'not_found'
    | 'full'
    | 'not_started'
    | 'rate_limited'
    | 'already_in_team'
    | 'unknown'
    | null;

  /** Create-path: locally typed name (committed via PATCH on submit). */
  teamName: string;
  teamNameError: 'forbidden' | 'unknown' | null;
  /** Optional raw server-detail string for diagnostic display next to the
   * inline teamNameError; keeps the i18n message visible while also
   * surfacing the underlying backend reason (403 detail, 422 validation, …). */
  teamNameErrorDetail: string | null;

  /** Backend-bound — populated after the team is created or joined. */
  teamId: string | null;
  teamShareCode: string | null;
  teamNameFromServer: string | null;
  /** Run UUID returned alongside the join — used for PATCH and WS subscription. */
  runId: string | null;

  /** True if this user created the team (used for routing & RBAC). */
  isCreator: boolean;
}

export interface WizardState {
  runCode: string | null;
  organizationSlug: string | null;
  displayName: string;
  guestMode: boolean;
  email: string;
  password: string;
  pathKind: WizardPathKind;

  /** Spec 027 — team-flow nested state. */
  teamFlow: TeamFlowState;
}

export interface WizardStateActions {
  setDisplayName(name: string): void;
  setGuestMode(on: boolean): void;
  setEmail(email: string): void;
  setPassword(pw: string): void;
  setOrganizationSlug(slug: string | null): void;
  setRunCode(code: string | null): void;
  setPathKind(kind: WizardPathKind): void;
  /** Spec 027 — partial update of team-flow state. */
  patchTeamFlow(patch: Partial<TeamFlowState>): void;
  resetTeamFlow(): void;
  reset(): void;
}

export type WizardStateContextValue = WizardState & WizardStateActions;

const SESSION_KEY_DISPLAY_NAME = 'clavis_wizard_displayName';
const SESSION_KEY_ORG_SLUG = 'clavis_wizard_org_slug';
const SESSION_KEY_TEAM_FLOW = 'clavis_wizard_team_state';

const emptyTeamFlow = (): TeamFlowState => ({
  mode: null,
  enteredCode: '',
  joinError: null,
  teamName: '',
  teamNameError: null,
  teamNameErrorDetail: null,
  teamId: null,
  teamShareCode: null,
  teamNameFromServer: null,
  runId: null,
  isCreator: false,
});

const initialState = (): WizardState => {
  let displayName = '';
  let organizationSlug: string | null = null;
  let teamFlow: TeamFlowState = emptyTeamFlow();
  if (typeof window !== 'undefined' && window.sessionStorage) {
    try {
      displayName = window.sessionStorage.getItem(SESSION_KEY_DISPLAY_NAME) ?? '';
      organizationSlug =
        window.sessionStorage.getItem(SESSION_KEY_ORG_SLUG) || null;
      const rawTeam = window.sessionStorage.getItem(SESSION_KEY_TEAM_FLOW);
      if (rawTeam) {
        const parsed = JSON.parse(rawTeam) as Partial<TeamFlowState>;
        teamFlow = { ...emptyTeamFlow(), ...parsed };
      }
    } catch {
      // ignore — privacy-mode browsers etc.
    }
  }
  return {
    runCode: null,
    organizationSlug,
    displayName,
    guestMode: false,
    email: '',
    password: '',
    pathKind: 'no-code',
    teamFlow,
  };
};

// eslint-disable-next-line react-refresh/only-export-components
export const WizardStateContext = createContext<WizardStateContextValue | null>(
  null,
);

interface ProviderProps {
  children: React.ReactNode;
}

export const WizardStateProvider: React.FC<ProviderProps> = ({ children }) => {
  const [state, setState] = useState<WizardState>(initialState);
  const { isAuthenticated, user } = useAuth();
  const wasAuthenticated = useRef(isAuthenticated);

  // Reset on logout (auth → unauth transition).
  useEffect(() => {
    if (wasAuthenticated.current && !isAuthenticated) {
      setState(initialState());
    }
    wasAuthenticated.current = isAuthenticated;
  }, [isAuthenticated]);

  // Backfill organizationSlug from the authenticated user.
  //
  // WelcomeStep + OrganizationPicker normally seed `state.organizationSlug`
  // during the wizard flow. But a user who lands directly on a later step
  // (deep-link, reload on /team/invite, etc.) skips those entry points and
  // `state.organizationSlug` stays null — silently breaking API calls that
  // require it (e.g. `advanceTeamStage` in InviteTeamStep, which is the
  // creator-side signal that drives joiners forward via the WS event).
  //
  // The authenticated user already carries `organization_slug` from
  // `/api/auth/me`, so use that as the source of truth whenever it's not
  // explicitly set by the wizard flow.
  useEffect(() => {
    if (user?.organization_slug && !state.organizationSlug) {
      setState((s) => ({ ...s, organizationSlug: user.organization_slug }));
    }
  }, [user?.organization_slug, state.organizationSlug]);

  // Selective sessionStorage sync — displayName + organizationSlug + team-flow.
  useEffect(() => {
    if (typeof window === 'undefined' || !window.sessionStorage) return;
    try {
      if (state.displayName) {
        window.sessionStorage.setItem(SESSION_KEY_DISPLAY_NAME, state.displayName);
      } else {
        window.sessionStorage.removeItem(SESSION_KEY_DISPLAY_NAME);
      }
      if (state.organizationSlug) {
        window.sessionStorage.setItem(SESSION_KEY_ORG_SLUG, state.organizationSlug);
      } else {
        window.sessionStorage.removeItem(SESSION_KEY_ORG_SLUG);
      }
      // Spec 027 — persist team-flow state (no PII, just IDs/codes/flags).
      const tf = state.teamFlow;
      const hasTeamProgress =
        tf.mode !== null ||
        tf.teamId !== null ||
        tf.teamShareCode !== null ||
        tf.enteredCode !== '' ||
        tf.teamName !== '';
      if (hasTeamProgress) {
        window.sessionStorage.setItem(SESSION_KEY_TEAM_FLOW, JSON.stringify(tf));
      } else {
        window.sessionStorage.removeItem(SESSION_KEY_TEAM_FLOW);
      }
    } catch {
      // ignore
    }
  }, [
    state.displayName,
    state.organizationSlug,
    state.teamFlow,
  ]);

  const actions = useMemo<WizardStateActions>(
    () => ({
      setDisplayName: (name) =>
        setState((s) => ({ ...s, displayName: name })),
      setGuestMode: (on) => setState((s) => ({ ...s, guestMode: on })),
      setEmail: (email) => setState((s) => ({ ...s, email })),
      setPassword: (password) => setState((s) => ({ ...s, password })),
      setOrganizationSlug: (slug) =>
        setState((s) => ({ ...s, organizationSlug: slug })),
      setRunCode: (code) => setState((s) => ({ ...s, runCode: code })),
      setPathKind: (kind) => setState((s) => ({ ...s, pathKind: kind })),
      patchTeamFlow: (patch) =>
        setState((s) => ({ ...s, teamFlow: { ...s.teamFlow, ...patch } })),
      resetTeamFlow: () =>
        setState((s) => ({ ...s, teamFlow: emptyTeamFlow() })),
      reset: () => {
        setState({
          runCode: null,
          organizationSlug: null,
          displayName: '',
          guestMode: false,
          email: '',
          password: '',
          pathKind: 'no-code',
          teamFlow: emptyTeamFlow(),
        });
        if (typeof window !== 'undefined' && window.sessionStorage) {
          try {
            window.sessionStorage.removeItem(SESSION_KEY_DISPLAY_NAME);
            window.sessionStorage.removeItem(SESSION_KEY_ORG_SLUG);
            window.sessionStorage.removeItem(SESSION_KEY_TEAM_FLOW);
          } catch {
            // ignore
          }
        }
      },
    }),
    [],
  );

  const value = useMemo<WizardStateContextValue>(
    () => ({ ...state, ...actions }),
    [state, actions],
  );

  return (
    <WizardStateContext.Provider value={value}>
      {children}
    </WizardStateContext.Provider>
  );
};
